import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';

import type { PaymentRunReviewRow } from '~entities/payment/api/reporting-payment-run-review';

import { Checkbox } from '~shared/shadcn/ui/checkbox';
import { cn } from '~shared/shadcn/lib/utils';

import { LOCK_CHECKBOX_CLASS_NAME } from './constants';
import { formatDueDateLabel, formatMoney } from './payment-run-format';
import type {
  PaymentRunReviewLockRowState,
  PaymentRunReviewMobileCardsProps,
} from './payment-run-review.types';
import { paymentRunRowBackgroundClass } from './payment-run-status';
import {
  PaymentMethodCell,
  PaymentRunLockCheckbox,
  StatusCell,
} from './payment-run-table-cells';

function MobileDetailRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className='flex items-start justify-between gap-3'>
      <dt className='w-[38%] shrink-0 text-sm leading-5 text-muted-foreground'>
        {label}
      </dt>
      <dd className='min-w-0 flex-1 break-words text-right text-sm leading-5 text-foreground [overflow-wrap:anywhere]'>
        {children}
      </dd>
    </div>
  );
}

function PaymentRunReviewMobileCard({
  row,
  lockState,
  onToggleLockSelection,
  onOpenRowDetail,
  isPostponed,
  currencySymbol,
}: {
  row: PaymentRunReviewRow;
  lockState: PaymentRunReviewLockRowState;
  onToggleLockSelection: (invoiceId: string, checked: boolean) => void;
  onOpenRowDetail: (row: PaymentRunReviewRow) => void;
  isPostponed: boolean;
  currencySymbol: string;
}) {
  const classSession =
    [row.class, row.session].filter(Boolean).join(' / ') || '—';

  return (
    <article
      className={cn(
        'rounded-xl border border-border/50 p-3.5',
        paymentRunRowBackgroundClass(row.status),
      )}
    >
      <div className='mb-2 flex items-start gap-2'>
        <PaymentRunLockCheckbox
          {...lockState}
          parentLabel={row.parent}
          onToggleLockSelection={onToggleLockSelection}
        />
        <div className='min-w-0 flex-1'>
          {row.parentId ? (
            <Link
              to='/family'
              search={{ parentId: row.parentId }}
              className='break-words text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline'
            >
              {row.parent || '—'}
            </Link>
          ) : (
            <p className='break-words text-sm font-semibold'>
              {row.parent || '—'}
            </p>
          )}
        </div>
        <button
          type='button'
          className='inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
          onClick={() => {
            onOpenRowDetail(row);
          }}
          aria-label={`View details for ${row.parent || 'charge'}`}
        >
          <ChevronRight className='size-4' aria-hidden />
        </button>
      </div>
      <dl className='space-y-1.5'>
        <MobileDetailRow label='Student'>
          {row.studentId ? (
            <Link
              to='/students/$studentId/new-payments'
              params={{ studentId: row.studentId }}
              className='text-blue-600 hover:text-blue-700 hover:underline'
            >
              {row.student || '—'}
            </Link>
          ) : (
            row.student || '—'
          )}
        </MobileDetailRow>
        <MobileDetailRow label='Due Date'>
          {row.dueDate ? formatDueDateLabel(row.dueDate) : '—'}
        </MobileDetailRow>
        <MobileDetailRow label='Class / Session'>{classSession}</MobileDetailRow>
        <MobileDetailRow label='Type'>
          {row.billingType.trim() ? row.billingType : '—'}
        </MobileDetailRow>
        <MobileDetailRow label='Base'>
          {formatMoney(row.baseAmount, currencySymbol)}
        </MobileDetailRow>
        <MobileDetailRow label='Discount'>
          {formatMoney(row.discount, currencySymbol)}
        </MobileDetailRow>
        <MobileDetailRow label='Final'>
          {formatMoney(row.finalAmount, currencySymbol)}
        </MobileDetailRow>
        <MobileDetailRow label='Transaction Fee'>
          {formatMoney(row.transactionFee, currencySymbol)}
        </MobileDetailRow>
        <MobileDetailRow label='Paid Date'>
          {row.paidDate ? formatDueDateLabel(row.paidDate) : '—'}
        </MobileDetailRow>
        <MobileDetailRow label='Paid Amount'>
          {row.paidAmount > 0
            ? formatMoney(row.paidAmount, currencySymbol)
            : '—'}
        </MobileDetailRow>
        <MobileDetailRow label='Payment'>
          <PaymentMethodCell
            paymentMethod={row.paymentMethod}
            cardLast4={row.cardLast4}
          />
        </MobileDetailRow>
        <MobileDetailRow label='Status'>
          <div className='flex flex-wrap items-center justify-end gap-1.5'>
            <StatusCell
              status={row.status}
              failureReason={row.failureReason}
            />
            {isPostponed ? (
              <span className='inline-flex items-center rounded-md bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800'>
                Postponed
              </span>
            ) : null}
          </div>
        </MobileDetailRow>
      </dl>
    </article>
  );
}

export function PaymentRunReviewMobileCards({
  rows,
  lockHeaderAllChecked,
  lockHeaderIndeterminate,
  onToggleSelectAllLockable,
  lockInteractionsDisabled,
  lockableSelectableForBulkCount,
  lockSelectionIds,
  incrementalLockAllowed,
  approvedForRunSet,
  lockableIds,
  onToggleLockSelection,
  onOpenRowDetail,
  isPostponed,
  currencySymbol,
}: PaymentRunReviewMobileCardsProps) {
  if (rows.length === 0) {
    return (
      <p className='px-4 py-8 text-center text-sm text-muted-foreground md:hidden'>
        No rows match the current filter.
      </p>
    );
  }

  return (
    <div className='space-y-3 px-4 py-3 md:hidden'>
      <div className='flex items-center gap-2 rounded-xl border border-border/50 px-3.5 py-2.5'>
        <Checkbox
          aria-label='Select all lockable charges in current filter'
          checked={lockHeaderAllChecked}
          indeterminate={lockHeaderIndeterminate}
          onCheckedChange={() => {
            onToggleSelectAllLockable();
          }}
          disabled={
            lockInteractionsDisabled || lockableSelectableForBulkCount === 0
          }
          className={LOCK_CHECKBOX_CLASS_NAME}
        />
        <span className='text-sm font-medium'>Select all</span>
      </div>
      {rows.map((row) => (
        <PaymentRunReviewMobileCard
          key={row.id}
          row={row}
          lockState={{
            invoiceId: row.id,
            lockSelectionIds,
            incrementalLockAllowed,
            approvedForRunSet,
            lockableIds,
            lockInteractionsDisabled,
          }}
          onToggleLockSelection={onToggleLockSelection}
          onOpenRowDetail={onOpenRowDetail}
          isPostponed={isPostponed}
          currencySymbol={currencySymbol}
        />
      ))}
    </div>
  );
}
